import { Injectable } from '@angular/core';
import { OIDC_STORAGE_PREFIX } from './oidc-storage.service';

export interface SessionControl {
  epoch: string;
  subject?: string;
  logout?: { state: string; startedAt: number };
}

export class OidcSessionEndedError extends Error {}

/** Same-origin locking and session identity, independent of the OIDC protocol. */
@Injectable({ providedIn: 'root' })
export class OidcSessionCoordinationService {
  readonly controlKey = OIDC_STORAGE_PREFIX + 'control';
  private readonly tabKey = OIDC_STORAGE_PREFIX + 'tab';

  readControl(): SessionControl {
    const value = localStorage.getItem(this.controlKey);
    return value ? JSON.parse(value) : { epoch: 'initial' };
  }

  // Callers hold the session lock while changing shared control state.
  writeControl(control: SessionControl): void {
    localStorage.setItem(this.controlKey, JSON.stringify(control));
  }

  assertEpoch(epoch: string): void {
    const current = this.readControl();
    if (current.epoch !== epoch || current.logout) throw new OidcSessionEndedError();
  }

  assertLogout(expected: SessionControl): void {
    const current = this.readControl();
    if (!expected.logout || current.epoch !== expected.epoch || current.logout?.state !== expected.logout.state) {
      throw new OidcSessionEndedError();
    }
  }

  exclusive<T>(action: () => Promise<T>): Promise<T> {
    if (!navigator.locks) return Promise.reject(new Error('This browser does not support secure cross-tab session coordination.'));
    return navigator.locks.request(OIDC_STORAGE_PREFIX + 'session-lock', action);
  }

  async waitForLogout(): Promise<void> {
    const logout = this.readControl().logout;
    if (!logout) return;
    await new Promise<void>((resolve, reject) => {
      const poll = window.setInterval(() => {
        if (!this.readControl().logout) {
          clearInterval(poll);
          resolve();
        } else if (Date.now() - logout.startedAt > 30000) {
          clearInterval(poll);
          reject(new Error('Another tab has not finished signing out.'));
        }
      }, 200);
    });
  }

  async claimTab(discardClonedSession: () => void): Promise<void> {
    // Tabs opened from this app can inherit sessionStorage. The live document's
    // lock distinguishes a clone from a reload before any copied tokens are used.
    if (!navigator.locks) throw new Error('Web Locks are required for OIDC sessions.');
    const id = sessionStorage.getItem(this.tabKey) || crypto.randomUUID();
    sessionStorage.setItem(this.tabKey, id);
    await new Promise<void>((resolve, reject) => {
      void navigator.locks.request(OIDC_STORAGE_PREFIX + 'tab:' + id, { ifAvailable: true }, async lock => {
        if (!lock) {
          discardClonedSession();
          sessionStorage.removeItem(this.tabKey);
          await this.claimTab(discardClonedSession);
          resolve();
          return;
        }
        resolve();
        await new Promise<void>(release => window.addEventListener('pagehide', () => release(), { once: true }));
      }).catch(reject);
    });
  }
}
